import React, { useCallback, useEffect, useState } from 'react';
import { Button, Divider } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Form } from 'formik-antd';

import ClientBulkImportUploader from '../ClientBulkImportUploader';
import SubmitButton from 'components/formik/SubmitButton';
import {
  getAllCustomersCompanies,
  getClientBulkImportTemplate,
  preUploadBulkImportCustomers,
} from 'redux/reducers/clientSlice';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { CreateCustomerRequest } from 'api/backofficeApiClient';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import { ResourceDownloadResponse } from 'api/backofficeApiClient';
import { downloadFile } from 'utils/fileUtils';
import { CompanySelectModel } from 'api/adminApiClient';
import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';

import styles from '../ClientBulkImportPage.module.less';

const validationSchema = Yup.object({
  file: Yup.array().min(1, 'Field is required').required('Field is required'),
  memberId: Yup.string().required('Field is required'),
});

interface ClientManualFileModel {
  fileName?: string;
  data?: any;
}

export interface FormClientBulkImport {
  file: ClientManualFileModel[];
  memberId?: string;
}

const initialValues: FormClientBulkImport = { file: [], memberId: undefined };

type Props = {
  onSuccess: (customers: CreateCustomerRequest[], memberId: string) => void;
};

export default function ClientUploadSection(props: Props) {
  const { onSuccess } = props;
  const dispatch = useAppDispatch();
  const [companies, setCompanies] = useState<CompanySelectModel[]>([]);
  const [isCompaniesLoaded, setIsCompaniesLoaded] = useState(false);

  const loadCompanies = useCallback(async () => {
    const result = await dispatch(getAllCustomersCompanies());

    if (getAllCustomersCompanies.fulfilled.match(result)) {
      setCompanies(result.payload);
    }
    setIsCompaniesLoaded(true);
  }, [dispatch]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const downloadCustomerFile = async (
    fileOptions: ResourceDownloadResponse,
  ) => {
    try {
      await downloadFile({
        downloadUrl: fileOptions.downloadUrl,
        fileName: fileOptions.fileName,
      });
    } catch (e: any) {
      BaseAlert.error(e.message);
    }
  };

  const handleDownloadTemplate = async () => {
    const result = await dispatch(getClientBulkImportTemplate());

    if (getClientBulkImportTemplate.fulfilled.match(result)) {
      await downloadCustomerFile(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleSubmitError = (result: any) => {
    if (result.payload?.errorMessage || result.payload?.errorDetails) {
      BaseAlert.error(result.payload?.errorMessage || ApiErrorMessage);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const onSubmit = async (values: FormClientBulkImport) => {
    const data = values.file[0];
    const result = await dispatch(preUploadBulkImportCustomers(data));

    if (preUploadBulkImportCustomers.fulfilled.match(result)) {
      const memberId = values.memberId;
      memberId && onSuccess(result.payload, memberId);
    } else {
      handleSubmitError(result);
    }
  };

  return (
    <>
      <BasePageLabel label="Upload file" />

      <div className="page-box page-inner">
        <div className="page-inner-container">
          <div className={styles.wrapper}>
            <div className={styles.box}>
              <div>
                1. Please download the template which contains all the fields
                required for creating Customer records in bulk. Click on
                'Download Template' to begin the download process.
              </div>

              <div>
                <Button
                  className="with-icon"
                  onClick={handleDownloadTemplate}
                  icon={<span className="icon icon-download" />}
                >
                  Download template
                </Button>
              </div>
            </div>
            <Divider />
            <div className={styles.box}>
              <div>2. Upload Instructions:</div>
              <ul>
                <li>
                  <strong>Mandatory fields</strong>: Pay attention to the
                  asterisk sign (*) in the column headers. This means the column
                  is mandatory. Leaving it blank may result in failed creation.
                </li>
                <li>
                  <strong>Validation Rules</strong>: The validation rules on the
                  columns should be met in order to create the customers. See
                  the description of each rule in the column comments.
                </li>
                <li>
                  Make use of the drop-down lists that contain items for
                  different columns such as Country, Potential headcount,
                  Industry, etc.
                </li>
                <li>
                  Make sure to fill in all the fields accurately and completely.
                </li>
                <li>
                  Save the file and go back to the {''}
                  <strong>Bulk import page.</strong>
                </li>
                <li>
                  Upload the file from your computer and click {''}
                  <strong>Next.</strong>
                </li>
                <li>
                  If you have either Member or Customer agreement types for your
                  records, you will be additionally asked to upload the
                  corresponding agreements. Otherwise, the system will proceed
                  to import without them.
                </li>
              </ul>
            </div>
            <Divider />
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              <Form layout="vertical">
                <div className={styles.box}>
                  <div>3. Select Member you want to create Customers to:</div>
                  <div className={styles.fieldWrap}>
                    <FormItem name="memberId">
                      <FormikSelectWithLoading
                        name="memberId"
                        isLoading={!isCompaniesLoaded}
                        className="full-width"
                        optionFilterProp="children"
                        placeholder="Select member"
                        showSearch
                      >
                        {companies.map(({ companyId, companyName }) => (
                          <FormikSelectWithLoading.Option
                            key={companyId}
                            value={companyId || ''}
                          >
                            {companyName}
                          </FormikSelectWithLoading.Option>
                        ))}
                      </FormikSelectWithLoading>
                    </FormItem>
                  </div>
                </div>
                <Divider />
                <div className={styles.box}>
                  <div>4. Upload the File</div>
                  <div>
                    Select the file you want to upload from your computer.
                  </div>

                  <div className="upload-wrapper">
                    <ClientBulkImportUploader fieldName="file" />
                    <div>
                      <SubmitButton className="with-icon">
                        Next <span className="icon icon-double-right" />
                      </SubmitButton>
                    </div>
                  </div>
                </div>
              </Form>
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
}
